// src/services/financeService.js

import api from './apiService';

// ===================================================================================
// ALTERAÇÃO PARA SUPORTE A CACHE BUSTING
// ===================================================================================

/**
 * Busca todos os dados consolidados para o dashboard 'Meu Desempenho' de uma só vez.
 * @param {number} ano - O ano de referência.
 * @param {number} mes - O mês de referência.
 * @param {number[]} contasIds - Array de IDs das contas a serem incluídas.
 * @param {number[]} cartoesIds - Array de IDs dos cartões a serem incluídos.
 * @param {boolean} forceRefresh - Se true, força o backend a ignorar o cache.
 * @param {'competencia'|'fatura'} [regime='fatura'] - Como atribuir despesas ao mês.
 * @returns {Promise<object>} Uma promessa que resolve para o objeto completo de dados do dashboard.
 */
export const getFullDashboardData = async (
    ano,
    mes,
    contasIds = [],
    cartoesIds = [],
    forceRefresh = false,
    regime = 'fatura'
) => {
    const params = {
        ano,
        mes,
        contas_ids: contasIds.join(','),
        cartoes_ids: cartoesIds.join(','),
        force_refresh: forceRefresh,
        regime,
    };
    const response = await api.get('/financeiro/meu-desempenho/', { params });
    return response.data;
};

/**
 * Insights financeiros (passado/presente/futuro). Preferível via getFullDashboardData().insights;
 * este endpoint reutiliza o mesmo cache do Meu Desempenho.
 */
export const getInsightsFinanceiros = async (
    ano,
    mes,
    contasIds = [],
    cartoesIds = [],
    regime = 'fatura'
) => {
    const params = {
        ano,
        mes,
        contas_ids: contasIds.join(','),
        cartoes_ids: cartoesIds.join(','),
        regime,
    };
    const response = await api.get('/financeiro/insights/', { params });
    return response.data;
};

// ... restante do arquivo financeService.js (mantém-se igual)
export const getContas = async (status = 'ATIVO') => {
    const response = await api.get('/financeiro/contas/', { params: { status } });
    return response.data;
};

export const getContaDetail = async (id) => {
    const response = await api.get(`/financeiro/contas/${id}/`);
    return response.data;
};

// Funções de Bancos
export const getBancos = async () => {
    const response = await api.get('/financeiro/bancos/');
    return response.data;
};

// Funções de Cartões de Crédito e Faturas
export const getCartoes = async () => {
    const response = await api.get('/financeiro/cartoes/');
    return response.data;
};
export const getCartaoDetail = async (id) => {
    const response = await api.get(`/financeiro/cartoes/${id}/`);
    return response.data;
};
export const createCartao = async (cartaoData) => {
    const response = await api.post('/financeiro/cartoes/', cartaoData);
    return response.data;
};
export const updateCartao = async (id, cartaoData) => {
    const response = await api.put(`/financeiro/cartoes/${id}/`, cartaoData);
    return response.data;
};
export const deleteCartao = async (id) => {
    const response = await api.delete(`/financeiro/cartoes/${id}/`);
    return response.data;
};
export const getFaturaDetail = async (cartaoId, ano, mes) => {
    const response = await api.get(`/financeiro/cartoes/${cartaoId}/faturas/${ano}/${mes}/`);
    return response.data;
}
export const getFaturasDisponiveis = async (cartaoId, faturaContextoId) => {
    let url = `/financeiro/faturas/disponiveis/cartao/${cartaoId}/`;

    if (faturaContextoId) {
        url += `?fatura_contexto_id=${faturaContextoId}`;
    }
    const response = await api.get(url);
    return response.data;
};
export const getProximasFaturas = async () => {
    const response = await api.get('/financeiro/faturas/proximas/');
    return response.data;
};
export const getFaturasResumo = async (params) => {
    const response = await api.get('/financeiro/faturas-resumo/', { params });
    return response.data;
};
export const pagarFatura = async (faturaId, dadosPagamento) => {
    const response = await api.post(`/financeiro/faturas/${faturaId}/pagar/`, dadosPagamento);
    return response.data;
};
export const reabrirFatura = async (faturaId) => {
    const response = await api.post(`/financeiro/faturas/${faturaId}/reabrir/`);
    return response.data;
};
export const getFaturaContextoInicial = async (cartaoId) => {
    const response = await api.get(`/financeiro/cartoes/${cartaoId}/contexto-inicial/`);
    return response.data;
};


// Funções para Cartões Adicionais
export const getCartoesAdicionais = async (cartaoId, status = null) => {
    let url = `/financeiro/cartoes-adicionais/?cartao_id=${cartaoId}`;
    if (status) {
        url += `&status=${status}`;
    }
    const response = await api.get(url);
    return response.data;
};

/** Todos os adicionais do usuário (ativos + arquivados), de todos os cartões. */
export const getTodosCartoesAdicionais = async () => {
    const [ativos, arquivados] = await Promise.all([
        api.get('/financeiro/cartoes-adicionais/', { params: { status: 'ATIVO' } }),
        api.get('/financeiro/cartoes-adicionais/', { params: { status: 'ARQUIVADO' } }),
    ]);
    const lista = (r) => (Array.isArray(r.data) ? r.data : r.data?.results || []);
    return [...lista(ativos), ...lista(arquivados)];
};

export const createCartaoAdicional = async (cartaoAdicionalData) => {
    const response = await api.post('/financeiro/cartoes-adicionais/', cartaoAdicionalData);
    return response.data;
};

export const updateCartaoAdicional = async (id, cartaoAdicionalData) => {
    const response = await api.put(`/financeiro/cartoes-adicionais/${id}/`, cartaoAdicionalData);
    return response.data;
};

export const deleteCartaoAdicional = async (id) => {
    const response = await api.delete(`/financeiro/cartoes-adicionais/${id}/`);
    return response;
};

export const reactivateCartaoAdicional = async (id) => {
    const response = await api.post(`/financeiro/cartoes-adicionais/${id}/reativar_cartao/`);
    return response.data;
};

/**
 * Relatório de gastos de cartão por portador (cartão adicional) em um período.
 * @param {object} params - { start_date, end_date, cartoes, regime, group_by }
 */
export const getRelatorioPorPortador = async (params = {}) => {
    const response = await api.get('/financeiro/relatorios/por-portador/', { params });
    return response.data;
};

// Funções para Contas a Receber (Fase 3)
export const getGastosPortadorFatura = async (faturaId) => {
    const response = await api.get(`/financeiro/faturas/${faturaId}/gastos-portador/`);
    return response.data;
};
export const gerarContaReceber = async (dados) => {
    const response = await api.post('/financeiro/contas-a-receber/gerar/', dados);
    return response.data;
};


// Funções de Categorias
export const getCategorias = async () => {
    const response = await api.get('/financeiro/categorias/');
    return response.data;
};
export const createCategoria = async (categoriaData) => {
    const response = await api.post('/financeiro/categorias/', categoriaData);
    return response.data;
};
export const updateCategoria = async (id, categoriaData) => {
    const response = await api.put(`/financeiro/categorias/${id}/`, categoriaData);
    return response.data;
};
export const deleteCategoria = async (id) => {
    const response = await api.delete(`/financeiro/categorias/${id}/`);
    return response.data;
};

// Funções de Transações
export const getTransacoes = async (params = {}) => {
    const response = await api.get('/financeiro/transacoes/', { params });
    return response.data;
};
export const getTransacaoById = async (id) => {
    const response = await api.get(`/financeiro/transacoes/${id}/`);
    return response.data;
};
export const getTransacoesPorConta = async (contaId, params = {}) => {
    const allParams = { ...params, conta: contaId };
    return getTransacoes(allParams);
};
export const createTransacao = async (transacaoData) => {
    const response = await api.post('/financeiro/transacoes/', transacaoData);
    return response.data;
};
export const updateTransacao = async (id, transacaoData) => {
    const response = await api.put(`/financeiro/transacoes/${id}/`, transacaoData);
    return response.data;
};
export const deleteTransacao = async (id, scope = null) => {
    let url = `/financeiro/transacoes/${id}/`;
    if (scope) {
        url += `?scope=${scope}`;
    }
    const response = await api.delete(url);
    return response.data;
};

export const marcarTransacaoComoPaga = async (id, dadosPagamento) => {
    const response = await api.post(`/financeiro/transacoes/${id}/marcar_como_pago/`, dadosPagamento);
    return response.data;
};

export const reverterTransacaoParaPendente = async (id) => {
    const response = await api.post(`/financeiro/transacoes/${id}/reverter_para_pendente/`);
    return response.data;
};


// Funções para Pagamento Parcial
export const createPagamentoParcial = async (pagamentoData) => {
    const response = await api.post('/financeiro/pagamentos-parciais/', pagamentoData);
    return response.data;
};
export const updatePagamentoParcial = async (pagamentoId, pagamentoData) => {
    const response = await api.put(`/financeiro/pagamentos-parciais/${pagamentoId}/`, pagamentoData);
    return response.data;
};
export const deletePagamentoParcial = async (transacaoCreditoId) => {
    const response = await api.delete(`/financeiro/pagamentos-parciais/${transacaoCreditoId}/`);
    return response.data;
};


// Funções de Transações Específicas de Cartão
export const createCompraParcelada = async (compraData) => {
try {
    const response = await api.post('/financeiro/compras-parceladas/', compraData);
    return response.data;
} catch (error) {
    console.error("Erro ao criar compra parcelada:", error.response?.data || error.message);
    throw error;
}
};
export const createEstorno = async (estornoData) => {
    try {
        const response = await api.post('/financeiro/estornos/', estornoData);
        return response.data;
    } catch (error) {
        console.error("Erro ao criar estorno:", error.response?.data || error.message);
        throw error;
    }
};
export const createEncargo = async (encargoData) => {
    try {
        const response = await api.post('/financeiro/transacoes/encargo/', encargoData);
        return response.data;
    } catch (error) {
        console.error("Erro ao criar encargo:", error.response?.data || error.message);
        throw error;
    }
};
export const updateEncargo = async (encargoId, encargoData) => {
    try {
        const response = await api.put(`/financeiro/transacoes/encargo/${encargoId}/`, encargoData);
        return response.data;
    } catch (error) {
        console.error("Erro ao atualizar encargo:", error.response?.data || error.message);
        throw error;
    }
};

// Funções de Recorrências
export const getRecorrencias = async () => {
    const response = await api.get('/financeiro/recorrencias/');
    return response.data;
};
export const createRecorrencia = async (recorrenciaData) => {
    const response = await api.post('/financeiro/recorrencias/', recorrenciaData);
    return response.data;
};
export const updateRecorrencia = async (id, recorrenciaData) => {
    const response = await api.put(`/financeiro/recorrencias/${id}/`, recorrenciaData);
    return response.data;
};
export const deleteRecorrencia = async (id) => {
    const response = await api.delete(`/financeiro/recorrencias/${id}/`);
    return response.data;
};

// Funções de Exceções de Recorrência
export const createRecorrenciaExcecao = async (excecaoData) => {
    const response = await api.post('/financeiro/recorrencias-excecoes/', excecaoData);
    return response.data;
};

// Outras funções
export const getMetas = async () => {
    const response = await api.get('/financeiro/metas/');
    return response.data;
};
export const createMeta = async (metaData) => {
    const response = await api.post('/financeiro/metas/', metaData);
    return response.data;
};
export const getPerformanceReport = async (mes, ano) => {
    const response = await api.get(`/financeiro/performance-report/?mes=${mes}&ano=${ano}`);
    return response.data;
};